import React, {
  forwardRef,
  Ref,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import { FaPencilAlt, FaTrashAlt } from 'react-icons/fa';
import { useToast } from '../../../hooks/toast';
import api from '../../../services/api';
import Button from '../../../components/Button';
import Loading from '../../../components/Loading';
import Paginacao from '../../../components/Paginacao';

import Modal from '../../../components/Modal';

import { IFormBusca } from '../index';
import AgendasCadastroPagina, { IConfiguracaoAgenda } from '../AgendasCadastro';
import getResponseErrors from '../../../utils/getResponseErrors';

export interface IPaginacao {
  termoBusca: IFormBusca;
  limite: number;
  pagina: number;
}

export interface IListagemAgendas {
  ConCodigo: number;
  ConNome: string;
  ConAtivo: boolean;
  ConOnline?: boolean;
  ConPerfil: string;
  CliNomeAbreviado: string;
  UsuNome: string;
  HoraAlmoco: string;
  ConTempoPadrao: number;
  ConAtende: boolean;
  ConMedicoAgendaCompleta: boolean;
  ConIntegracao: number;
}

interface IAgendas
  extends Omit<IListagemAgendas, 'ConMedicoAgendaCompleta' | 'ConIntegracao'> {
  atendeFormatado?: string;
  ativoFormatado?: string;
  onlineFormatado?: string;
}

export interface IDadosLista {
  perfil?: string;
  medico?: string;
  clinica?: string;
}

export interface IRetornoListagemAgenda {
  agendas: IListagemAgendas[];
  total: number;
}
export interface RefObject {
  addAgenda(
    dadosConfiguracaoAgenda: IConfiguracaoAgenda,
    dadosLista: IDadosLista,
    conCodigo: number | undefined,
  ): Promise<void>;
}
type Props = {
  dados?: IFormBusca;
  ref: Ref<RefObject>;
};

const ListaAgendas: React.FC<Props> = forwardRef(
  (prop: Props, ref: Ref<RefObject>) => {
    const [showModalCadastro, setShowModalCadastro] = useState(false);
    const { addToast } = useToast();
    const [paginacao, setPaginacao] = useState<IPaginacao | undefined>(
      undefined,
    );
    const [loading, setLoading] = useState(false);
    const [totalRegistros, setTotalRegistros] = useState<number | undefined>(
      undefined,
    );
    const [listagemAgendas, setListagemAgendas] = useState<IAgendas[]>([]);

    const [agendaSelecionada, setAgendaSelecionada] = useState<
      number | undefined
    >(undefined);

    const [labels, setLabels] = useState<IDadosLista | undefined>(undefined);

    useEffect(() => {
      setPaginacao({
        termoBusca: prop.dados || {
          medico: '',
          perfil: '',
          situacao: 'true',
          online: '',
          atende: '',
          clinica: '',
        },
        pagina: 1,
        limite: 20,
      });
    }, [prop.dados]);

    useEffect(() => {
      async function buscaAgendas(): Promise<void> {
        if (paginacao) {
          try {
            setLoading(true);
            const {
              medico,
              perfil,
              situacao,
              online,
              clinica,
              atende,
            } = paginacao.termoBusca;
            let query = '';
            if (medico !== '') query = `&UsuCodigo=${medico}`;
            if (perfil !== '') query = `${query}&PerCodigo=${perfil}`;
            if (situacao !== '') query = `${query}&ConAtivo=${situacao}`;
            if (online !== '') query = `${query}&ConOnline=${online}`;
            if (clinica !== '') query = `${query}&ConClinica=${clinica}`;
            if (atende !== '') query = `${query}&ConAtende=${atende}`;

            const response = await api.get<IRetornoListagemAgenda>(
              `/listagemagendas?pagina=${paginacao.pagina}&limite=${paginacao.limite}${query}`,
            );

            const listagemAgendasApi: IAgendas[] = response.data.agendas;

            const listagemAgendasApiFormatada = listagemAgendasApi.map(
              lista => {
                return {
                  ...lista,
                  atendeFormatado: lista.ConAtende ? 'Sim' : 'Não',
                  ativoFormatado: lista.ConAtivo ? 'Sim' : 'Não',
                  onlineFormatado: lista.ConOnline ? 'Sim' : 'Não',
                };
              },
            );

            setTotalRegistros(response.data.total);

            setListagemAgendas(listagemAgendasApiFormatada);
          } catch (error) {
            const mensagemErro = getResponseErrors(error);
            addToast({
              title: 'Não foi possível listar agendas',
              type: 'error',
              description: mensagemErro,
            });
          } finally {
            setLoading(false);
          }
        }
      }
      if (paginacao) buscaAgendas();
    }, [paginacao, addToast]);

    const handleDeleteAgenda = useCallback(
      async (codigo: number) => {
        setLoading(true);
        try {
          await api.delete(`/agendas/${codigo}`);
          setListagemAgendas(AgendasState =>
            AgendasState?.filter(con => con.ConCodigo !== codigo),
          );
          addToast({
            type: 'success',
            title: 'Agenda excluída',
          });
        } catch (error) {
          const mensagemErro = getResponseErrors(error);
          addToast({
            title: 'Não foi possível deletar agenda',
            type: 'error',
            description: mensagemErro,
          });
        } finally {
          setLoading(false);
        }
      },
      [addToast],
    );

    const addAgenda = useCallback(
      async (
        dadosConfiguracaoAgenda: IConfiguracaoAgenda,
        dadosLista: IDadosLista,
        ConCodigo: number | undefined,
      ) => {
        let response;
        setLoading(true);
        try {
          if (ConCodigo) {
            response = await api.put<IConfiguracaoAgenda>(
              `/agendas/${ConCodigo}`,
              dadosConfiguracaoAgenda,
            );
          } else {
            response = await api.post<IConfiguracaoAgenda>(
              '/agendas',
              dadosConfiguracaoAgenda,
            );
          }
          const {
            ConCodigo: conCodigo,
            ConNome,
            ConAtivo,
            ConAtende,
            ConTempoPadrao,
            ConOnline,
          } = response.data;

          const retornoConfiguracaoAgenda: IAgendas = {
            ConCodigo: conCodigo,
            ConNome,
            ConAtivo,
            ConAtende,
            ConTempoPadrao,
            ConPerfil: dadosLista.perfil || '',
            CliNomeAbreviado: dadosLista.clinica || '',
            UsuNome: dadosLista.medico || '',
            HoraAlmoco: '',
            atendeFormatado: ConAtende ? 'Sim' : 'Não',
            ativoFormatado: ConAtivo ? 'Sim' : 'Não',
            onlineFormatado: ConOnline ? 'Sim' : 'Não',
          };

          setListagemAgendas(state => {
            return state
              ? [retornoConfiguracaoAgenda].concat(
                  state.filter(
                    config =>
                      config.ConCodigo !== retornoConfiguracaoAgenda.ConCodigo,
                  ),
                )
              : [retornoConfiguracaoAgenda];
          });
          setShowModalCadastro(false);
          addToast({ title: 'Agenda salvo', type: 'success' });
        } catch (error) {
          const mensagemErro = getResponseErrors(error);
          addToast({
            title: 'Não foi possível salvar agenda',
            type: 'error',
            description: mensagemErro,
            timeAlive: 10000,
          });
        } finally {
          setLoading(false);
        }
      },
      [addToast],
    );

    useImperativeHandle(ref, () => ({ addAgenda }));

    const handleProximaPagina = useCallback((page: number) => {
      setPaginacao(state => {
        return state && { ...state, pagina: page };
      });
    }, []);

    const handleEditar = useCallback(
      (conCodigo: number, nomeLabels: IDadosLista) => {
        setAgendaSelecionada(conCodigo);
        setShowModalCadastro(true);
        setLabels(nomeLabels);
      },
      [],
    );

    return (
      <>
        {showModalCadastro && (
          <Modal
            titulo="Cadastro de Agenda"
            handleFecharModal={() => setShowModalCadastro(false)}
            large
            show={showModalCadastro}
          >
            <AgendasCadastroPagina
              conCodigo={agendaSelecionada}
              addAgenda={addAgenda}
              labels={labels}
            />
          </Modal>
        )}
        {loading && <Loading />}

        {listagemAgendas ? (
          <>
            <table
              className="table table-hover table-sm table-font-small"
              style={{ borderWidth: '0px' }}
            >
              <tbody>
                <tr className="bg-light" style={{ fontSize: 'Small' }}>
                  <th scope="col">Perfil</th>
                  <th scope="col">Profissional</th>
                  <th scope="col">Nome Agenda</th>
                  <th scope="col">Temp. Consulta</th>
                  <th scope="col">Clínica</th>
                  <th scope="col">Atende Consultorio</th>
                  <th scope="col">Ativo</th>
                  <th scope="col">On-line</th>
                  <th scope="col">&nbsp;</th>
                  <th scope="col">&nbsp;</th>
                </tr>
                {listagemAgendas.map((lista: IAgendas) => (
                  <tr
                    key={lista.ConCodigo}
                    style={{ fontSize: 'Small', height: '30px' }}
                    className={`${!lista.ConAtivo ? 'text-muted' : ''}`}
                  >
                    <td>{lista.ConPerfil}</td>
                    <td>{lista.UsuNome}</td>
                    <td>{lista.ConNome}</td>
                    <td style={{ width: '70px' }}>{lista.ConTempoPadrao}</td>
                    <td>{lista.CliNomeAbreviado}</td>
                    <td style={{ width: '70px' }}>{lista.atendeFormatado}</td>
                    <td style={{ width: '60px' }}>{lista.ativoFormatado}</td>
                    <td style={{ width: '60px' }}>{lista.onlineFormatado}</td>
                    <td className="p-0" style={{ width: '40px' }}>
                      <Button
                        type="button"
                        variant="primary-outline"
                        className="py-0"
                        title={`${lista.ConCodigo}`}
                        onClick={
                          () =>
                            handleEditar(lista.ConCodigo, {
                              perfil: lista.ConPerfil,
                              medico: lista.UsuNome,
                              clinica: lista.CliNomeAbreviado,
                            })
                          // eslint-disable-next-line react/jsx-curly-newline
                        }
                      >
                        <FaPencilAlt />
                      </Button>
                    </td>

                    <td className="p-0" style={{ width: '40px' }}>
                      {/* // onClick="if (!window.confirm('Tem certetza que deseja
        excluir este Agenda?')) return false;" */}
                      <Button
                        type="button"
                        variant="secondary-outline"
                        className="py-0"
                        title={`${lista.ConCodigo}`}
                        onClick={() => {
                          handleDeleteAgenda(lista.ConCodigo);
                        }}
                      >
                        <FaTrashAlt />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Paginacao
              limitePagina={paginacao?.limite || 15}
              paginasVizinhas={2}
              totalRegistros={totalRegistros}
              paginaInicio={paginacao?.pagina}
              handleProximaPagina={handleProximaPagina}
            />
          </>
        ) : (
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ minHeight: '250px' }}
          >
            <h5>
              Pesquise agendas pelo Perfil, médico, atende consultório, clínica
              e/ou Situação
            </h5>
          </div>
        )}
      </>
    );
  },
);

export default ListaAgendas;
